import { useEffect, useState, Fragment } from "react";
import classNames from "classnames";
import { Typography, DropDown } from "components/atoms";
import { AIRecommendationSmallCard } from "components/molecules/AIRecommendationSmallCard";
import {
  AiRecommendationSearchBar,
  Carousel,
  FetchError,
} from "components/molecules";
import { useScreenSize } from "hooks";
import { useGetAiRecommendationsQuery } from "services/aiRecommendations";
import { Spinner } from "components/atoms/Icon";
import useGoogleAnalytics from "hooks/useGoogleAnalytics";
import { feature_toggle } from "featureToggle";
import { STUDY_RECOMMENDATIONS_SELECT_SUBJECT, STUDY_RECOMMENDATIONS_START_TOPIC } from "utils/constants/GoogleTagGUIDs";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

export const AiCardSection = ({ theme }: IProps) => {
  const screen = useScreenSize();

  const [subjectDropDownValue, setSubjectDropDownValue] = useState<
    null | string | { [key: number]: string }
  >(null);

  const [elementsPerSlide, setElementsPerSlide] = useState(3);

  const { trackEvent } = useGoogleAnalytics();

  const {
    data: aiRecommendData,
    isLoading: isAiRecommendLoading,
    isError: isAiRecommendError,
  } = useGetAiRecommendationsQuery();

  const [maxWeights, setMaxWeights] = useState<any>([]);

  const totalSlides = Math.ceil(maxWeights.length / elementsPerSlide);

  const slides = [];
  for (let i = 0; i < totalSlides; i++) {
    const startIndex = i * elementsPerSlide;
    const endIndex = Math.min(startIndex + elementsPerSlide, maxWeights.length);
    const slideElements = maxWeights.slice(startIndex, endIndex);
    slides.push(slideElements);
  }

  useEffect(() => {
    if (subjectDropDownValue === null) {
      if (
        aiRecommendData &&
        Array.isArray(aiRecommendData) &&
        aiRecommendData.length === 0
      ) {
        setMaxWeights([]);
      } else if (
        aiRecommendData &&
        Array.isArray(aiRecommendData) &&
        aiRecommendData.length > 0 &&
        aiRecommendData[0] &&
        aiRecommendData[0].RecommendedContentItems &&
        Array.isArray(aiRecommendData[0].RecommendedContentItems) &&
        aiRecommendData[0].RecommendedContentItems.length === 0
      ) {
        setMaxWeights([]);
      } else {
        setMaxWeights(
          aiRecommendData
            ? [
                ...Array.from(
                  new Map(
                    [...aiRecommendData[0]?.RecommendedContentItems]
                      .sort(
                        (first: any, second: any) =>
                          second.Weight - first.Weight
                      )
                      .map((item: any) => [
                        item.AssessmentObjective.Parent.Name,
                        item,
                      ])
                  ).values()
                ),
              ].slice(0, 4)
            : []
        );
      }
    } else if (subjectDropDownValue !== null) {
      let itemsArray: any = aiRecommendData[0].RecommendedContentItems.filter(
        (item: any) =>
          item.AssessmentObjective.Subject ===
          Object.values(subjectDropDownValue)[0]
      ).sort((first: any, second: any) => second.Weight - first.Weight);

      setMaxWeights(
        [
          ...Array.from(
            new Map(
              itemsArray.map((item: any) => [
                item.AssessmentObjective.Parent.Name,
                item,
              ])
            ).values()
          ),
        ].slice(0, 4)
      );

      // track google tag manager event
      feature_toggle.googleTagManager.isEnable === true && trackEvent("button_click", "Select Subject")
    }
  }, [subjectDropDownValue, aiRecommendData]);

  useEffect(() => {
    if (screen.width <= 514) {
      setElementsPerSlide(1);
    } else if (screen.width <= 766) {
      setElementsPerSlide(2);
    } else if (screen.width <= 1005) {
      setElementsPerSlide(3);
    } else if (screen.width <= 1024) {
      setElementsPerSlide(4);
    } else if (screen.width <= 1100) {
      setElementsPerSlide(2);
    } else if (screen.width <= 1350) {
      setElementsPerSlide(3);
    } else {
      setElementsPerSlide(4);
    }
  }, [screen.width]);

  return (
    <div className="ai-recommendation-wrapper">
      {isAiRecommendLoading ? (
        <div className="ai-charts-spinner-container">
          <Spinner theme={theme} />
        </div>
      ) : isAiRecommendError ? (
        <div className={classNames("ai-charts-error-container")}>
          <FetchError content="Oops! failed to load resources." />
        </div>
      ) : (aiRecommendData &&
          Array.isArray(aiRecommendData) &&
          aiRecommendData.length === 0) ||
        (aiRecommendData &&
          Array.isArray(aiRecommendData) &&
          aiRecommendData.length > 0 &&
          aiRecommendData[0].RecommendedContentItems &&
          Array.isArray(aiRecommendData[0].RecommendedContentItems) &&
          aiRecommendData[0].RecommendedContentItems.length === 0) ? (
        <div className={classNames("ai-charts-error-container")}>
          <Typography label="No results" variant="h5Med" />
        </div>
      ) : (
        <>
          {screen.width > 425 ? null : (
            <div className="mobile-title">
              <Typography variant="h4Bold" label="AI Study Recommendations" />
              <div className="beta-text-container">
                <Typography
                  variant="h7Bold"
                  label="BETA*"
                  styles="beta-text-styles"
                />
              </div>
            </div>
          )}
          <div className="ai-recommendation-container-top">
            {screen.width < 426 ? null : (
              <div className="first-title-section">
                <Typography variant="h3Bold" label="AI Study Recommendations" />
                <div className="beta-text-container">
                  <Typography
                    variant="h7Bold"
                    label="BETA*"
                    styles="beta-text-styles"
                  />
                </div>
              </div>
            )}
            <div className="second-search-and-description-section">
              <div className="search-section">
                {/* <AiRecommendationSearchBar /> */}
                <DropDown
                  data={
                    aiRecommendData &&
                    Array.isArray(aiRecommendData) &&
                    aiRecommendData.length > 0 &&
                    aiRecommendData[0].RecommendedContentItems
                      ? [
                          ...Array.from(
                            new Set<any>(
                              aiRecommendData[0].RecommendedContentItems.map(
                                (item: any) => item.AssessmentObjective.Subject
                              )
                            )
                          ),
                        ]
                      : []
                  }
                  placeholder="Select subjects to filter"
                  defaultSelected={subjectDropDownValue}
                  setDropDownSelection={setSubjectDropDownValue}
                  dataTrackingId={STUDY_RECOMMENDATIONS_SELECT_SUBJECT}
                />
              </div>
              <div className="description-section">
                <div className="title">
                  <Typography
                    variant="h6Medium"
                    label="Select the Topics you will like to work on:"
                    styles="typography-custom-style"
                  />
                </div>
                <div className="description">
                  <Typography
                    variant="h7Book"
                    label="These are your top recommended assessment objectives for you to work on"
                    styles="typography-custom-style"
                  />
                </div>
              </div>
            </div>
            <div className="circular-progress-bar-section">
              <Carousel renderStyle="aiRecommendation">
                {maxWeights.map((element: any, index: number) => (
                  <AIRecommendationSmallCard
                    key={index}
                    theme={theme}
                    data={element}
                    widgetTrackingId={STUDY_RECOMMENDATIONS_START_TOPIC}
                  />
                ))}
              </Carousel>
            </div>
            <div className="disclaimer-text-wrapper">
              <Typography label="*" variant="h9Book" styles="asteric-styles" />
              <Typography
                label="This feature is currently in beta testing, and we’re actively seeking feedback to improve it."
                variant="h9Book"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
